import React, { useEffect } from 'react';
import { Box, makeStyles, createStyles, Typography, Grid, Tooltip } from '@material-ui/core';
import { parseISO } from 'date-fns';
import {
  Admin,
  Accounting,
  AccountingManager,
  Contact,
  Reservation,
  roomTypeQuery,
  roomTypeService,
} from '@lib/state';
import {
  NameDisplay,
  EnumValueDisplay,
  DateDisplay,
  PhoneDisplay,
  Section,
  Link,
  ShowMoreButton,
  ButtonLink,
  CorporateAffiliationDisplay,
  useHasRole,
  useObservable,
  useLookup,
  DataTable,
  VipDisplay,
} from '@lib/common';
import { Tile, DownloadButton } from 'app/shared';
import { ReservationFilters, ReservationSearchFilter } from './reservation-search-filter';
import { CorporateAccount } from 'app/state';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const useStyles = makeStyles(theme =>
  createStyles({
    downloadButton: {
      marginRight: theme.spacing(2),
    },
    icon: {
      color: theme.palette.secondary.main,
    },
  })
);

interface Props {
  reservations: Array<Reservation>;
  propertyId: string;
  corporateAccounts: Array<CorporateAccount>;
  filters: ReservationFilters;
  onChange: (filters: ReservationFilters) => void;
  onExport: (filters: ReservationFilters) => void;
  loadMore: () => void;
  isDone: boolean;
  userId?: string;
  loading: boolean;
}

export const ReservationLookup: React.FC<Props> = ({
  reservations,
  propertyId,
  corporateAccounts,
  filters,
  onChange,
  onExport,
  loadMore,
  isDone,
  userId,
  loading,
}) => {
  const styles = useStyles();
  const isAccounting = useHasRole(Admin, Accounting, AccountingManager);

  useEffect(() => {
    if (propertyId) roomTypeService.getPropertyRoomTypes(propertyId);
  }, [propertyId]);

  const roomTypes = useObservable(roomTypeQuery.propertyRoomTypes);

  const roomTypeLookup = useLookup(roomTypes, x => x.id);

  const getEmail = (contact: Contact) => {
    return contact.email ?? contact.alternateEmail;
  };

  return (
    <Tile>
      <Box display="flex" justifyContent="flex-end" flexDirection="row">
        <DownloadButton onDownload={() => onExport(filters)} className={styles.downloadButton} />
        {isAccounting && (
          <ButtonLink
            to="/bulk-change-billing/new"
            color="primary"
            variant="outlined"
            style={{ marginRight: 8 }}
            data-testid="lookupBulkChangeBillingButton"
          >
            Bulk Change Billing
          </ButtonLink>
        )}
        <ButtonLink
          to="/reservations/add"
          color="primary"
          variant="contained"
          data-testid="lookupCreateReservationButton"
        >
          Create Reservation
        </ButtonLink>
      </Box>
      <ReservationSearchFilter
        filters={filters}
        roomTypes={roomTypes}
        corporateAccounts={corporateAccounts}
        onChange={onChange}
        userId={userId}
        propertyId={propertyId}
      />

      <Section title="Results" textAlign="left" disableGutters>
        <DataTable
          items={reservations || []}
          loading={loading}
          getItemKey={res => res.id}
          children={<ShowMoreButton isDone={isDone} loadMore={loadMore} />}
          columns={[
            {
              title: 'Reservation',
              valueFactory: res => <ReservationIdValueFactory res={res} />,
            },
            {
              title: 'Status',
              valueFactory: res => <EnumValueDisplay value={res.status} />,
            },
            {
              title: 'Company',
              valueFactory: res => <CorporateAffiliationDisplay {...res.affiliation} />,
              colSpan: 2,
            },
            {
              title: 'Guest Name',
              valueFactory: res => (
                <Grid container>
                  <Grid item xs={11}>
                    <NameDisplay
                      {...(res.contacts.length > 0 ? res.contacts[0].name : '')}
                      directory
                    />
                  </Grid>
                  <Grid item xs={1}>
                    {res.contacts.length > 0 ? (
                      <VipDisplay
                        vipStatus={res.contacts[0].vipStatus}
                        key={res.contacts[0].userId}
                      />
                    ) : (
                      ''
                    )}
                  </Grid>
                </Grid>
              ),
              colSpan: 2,
            },
            {
              title: 'Guest Email',
              valueFactory: res => (
                <Typography>
                  {res.contacts.length > 0 ? getEmail(res.contacts[0].contact) : ''}
                </Typography>
              ),
              colSpan: 2,
            },
            {
              title: 'Guest Phone',
              valueFactory: res => (
                <PhoneDisplay
                  phone={res.contacts.length > 0 ? res.contacts[0].contact.phone : ''}
                />
              ),
              colSpan: 2,
            },
            {
              title: 'Check In',
              valueFactory: res => (
                <DateDisplay date={parseISO(res.checkInDate)} dateFormat="M/d/yyyy" />
              ),
              colSpan: 2,
            },
            {
              title: 'Check Out',
              valueFactory: res => (
                <DateDisplay date={parseISO(res.checkOutDate)} dateFormat="M/d/yyyy" />
              ),
              colSpan: 2,
            },
            {
              title: 'Created At',
              valueFactory: res => (
                <DateDisplay date={parseISO(res.createdTime)} dateFormat="M/d/yyyy h:mm a" />
              ),
              colSpan: 2,
            },
            {
              title: 'Room Type',
              valueFactory: res => (
                <Typography>
                  {res.rooms.length > 0 ? roomTypeLookup.get(res.rooms[0].roomTypeId)?.name : ''}
                </Typography>
              ),
              colSpan: 2,
            },
          ]}
        />
      </Section>
    </Tile>
  );
};

interface ReservationIdValueProps {
  res: Reservation;
}

function ReservationIdValueFactory({ res }: ReservationIdValueProps) {
  const styles = useStyles();

  return (
    <Tooltip
      title={
        res.fields.some(c => c.isRequired && c.value === '') ? 'Missing cost center field info' : ''
      }
    >
      <Grid container>
        <Grid item xs={2}>
          {res.fields.some(c => c.isRequired && c.value === '') ? (
            <FontAwesomeIcon icon={'exclamation-circle'} className={styles.icon} />
          ) : undefined}
        </Grid>
        <Grid item xs={1} />
        <Grid item xs={9} alignContent="flex-end" alignItems="flex-end" justifyContent="flex-end">
          <Link to={`/reservations/${res.id}`}>{res.recordNumber}</Link>
        </Grid>
      </Grid>
    </Tooltip>
  );
}
